import { Tax } from '../services/apps/taxes/taxes.service';

/** Desglose por impuesto alineado a InvoiceLineTaxDetailsDto / UI. */
export interface LineTaxDisplay {
  taxName: string;
  ratePercent: number;
  isInclusive: boolean;
  taxType: string;
  baseAmount: number;
  taxAmount: number;
  fiscalCode?: string;
  fiscalSystem?: string;
}

export interface ComputedInvoiceLine {
  netUnitPrice: number;
  lineNetTotal: number;
  lineTaxTotal: number;
  lineTaxes: LineTaxDisplay[];
}

function roundMoney(v: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.round(n * 100) / 100;
}

function rateFraction(ratePoints: number): number {
  return ratePoints / 100;
}

export function normalizeTenantTaxMode(mode: string | null | undefined): 'None' | 'Ticket' | 'Electronic' | null {
  if (mode == null || mode === '') {
    return null;
  }
  const m = mode.trim();
  if (/^none$/i.test(m)) {
    return 'None';
  }
  if (/^ticket$/i.test(m)) {
    return 'Ticket';
  }
  if (/^electronic$/i.test(m)) {
    return 'Electronic';
  }
  return null;
}

/** Réplica de InvoiceTaxComputation.FilterTaxesForInvoice (Parking.API). */
export function filterTaxesForInvoice(mode: string | null | undefined, taxes: Tax[]): Tax[] {
  const n = normalizeTenantTaxMode(mode);
  if (n === 'None') {
    return [];
  }
  const list = taxes.filter(t => t.isActive && t.isPercent);
  if (n === 'Ticket') {
    return list
      .filter(t => t.isDisplayedOnInvoice)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }
  if (n === 'Electronic') {
    return list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }
  return [];
}

/** Réplica de InvoiceTaxComputation.ComputeLine: importe bruto = precio final × cantidad. */
export function computeInvoiceLine(quantity: number, grossLineAmount: number, applicableTaxes: Tax[]): ComputedInvoiceLine {
  const q = quantity > 0 ? quantity : 0;
  const gross = roundMoney(grossLineAmount);

  if (applicableTaxes.length === 0) {
    const net = roundMoney(gross);
    return {
      netUnitPrice: q > 0 ? roundMoney(net / q) : 0,
      lineNetTotal: net,
      lineTaxTotal: 0,
      lineTaxes: []
    };
  }

  const inclusive = applicableTaxes.filter(t => t.isInclusive);
  const exclusive = applicableTaxes.filter(t => !t.isInclusive);

  let pi = 1;
  for (const t of inclusive) {
    pi *= 1 + rateFraction(t.rate);
  }
  const sumExc = exclusive.reduce((s, t) => s + rateFraction(t.rate), 0);
  let divisor = pi + sumExc;
  if (divisor <= 0) {
    divisor = 1;
  }

  const lineNet = roundMoney(gross / divisor);
  const lineTaxes: LineTaxDisplay[] = applicableTaxes.map(tax => {
    const r = rateFraction(tax.rate);
    const baseAmt = lineNet;
    const taxAmt = roundMoney(lineNet * r);
    return {
      taxName: tax.name,
      ratePercent: tax.rate,
      isInclusive: tax.isInclusive,
      taxType: tax.taxType,
      baseAmount: baseAmt,
      taxAmount: taxAmt,
      fiscalCode: tax.fiscalCode,
      fiscalSystem: tax.fiscalSystem
    };
  });

  const lineTaxTotal = roundMoney(lineTaxes.reduce((s, x) => s + x.taxAmount, 0));

  return {
    netUnitPrice: q > 0 ? roundMoney(lineNet / q) : 0,
    lineNetTotal: lineNet,
    lineTaxTotal,
    lineTaxes
  };
}
